// pages/SupportPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";
import TicketList from "../components/support/TicketList";
import NewTicketForm from "../components/support/NewTicketForm";
import EmptyState from "../components/support/EmptyState";
import { useTranslation } from "react-i18next";
import { httpRequest } from "../utils/httpRequest";
import { toast } from "react-toastify";
import { BASE_URL } from "../config";

const SupportPage = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.includes("fa");

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const token = user?.token;

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchTickets = async () => {
    if (!token) return;
    setLoading(true);
    try {
      // لیست تیکت‌های خود کاربر
      const res = await httpRequest(
        "GET",
        "/ticket/my",
        {},
        {
          "x-access-token": token,
        }
      );

      const payload = res?.data?.data || res?.data || {};
      const items = Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(payload)
        ? payload
        : [];

      setTickets(items);
    } catch (e) {
      toast.error(e?.message || t("common.unknown_error"));
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ساخت تیکت جدید
  const handleAddTicket = async (formData) => {
    if (!token) return;

    try {
      if (!(formData instanceof FormData)) {
        throw new Error("Invalid payload: expected FormData");
      }

      const res = await fetch(`${BASE_URL}/ticket`, {
        method: "POST",
        headers: {
          "x-access-token": token,
          // ❌ Content-Type نذار! چون FormData هست و boundary باید خود fetch بذاره
        },
        body: formData,
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json?.message || "FAILED");
      }

      const created = json?.data || json;
      if (!created?._id) throw new Error("Ticket not created");

      toast.success(t("support.ticket_created", "تیکت ثبت شد"));

      await fetchTickets(); // ✅ لیست رو آپدیت کن

      return created;
    } catch (e) {
      toast.error(e?.message || t("common.unknown_error"));
      throw e;
    }
  };

  const mainContentClass = `flex-1 flex flex-col px-2 pb-5 lg:px-0 lg:pb-0 ${
    isRtl ? "md:mr-64" : "md:ml-64"
  }`;
  const mainSectionClass = `flex-1 overflow-y-auto p-4 md:p-6 bg-gray-100 rounded-[16px] ${
    isRtl ? "lg:me-3 lg:mb-3" : "lg:ms-3 lg:mb-3"
  }`;

  return (
    <div className="flex h-screen">
      <Helmet>
        <title>{t("support.page_title")}</title>
      </Helmet>

      <Sidebar />
      <div className={mainContentClass}>
        <Header />
        <main className={mainSectionClass}>
          <h1 className="text-2xl font-bold text-gray-800 mb-6">
            {t("support.page_header")}
          </h1>

          <NewTicketForm
            onSubmit={async (ticketForm) => {
              await handleAddTicket(ticketForm);
            }}
          />

          <div className="mt-8">
            <h2 className="text-lg font-semibold mb-3 text-gray-700">
              {t("support.your_tickets")}
            </h2>

            {loading ? (
              <div className="text-sm text-gray-600">
                {t("common.loading", "در حال بارگذاری...")}
              </div>
            ) : tickets.length > 0 ? (
              <TicketList tickets={tickets} />
            ) : (
              <EmptyState message={t("support.empty_state_message")} />
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SupportPage;
